// src/pages/AnalyticsPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import { Link, useOutletContext, useNavigate } from "react-router-dom"; 
import { useWallet } from "../contexts/WalletContext";
import { analyzeTokenData } from "../services/analyticsService"; 
import Button from "../components/common/Button";
import Spinner from "../components/common/Spinner";
import Input from "../components/common/Input"; 
import {
  DocumentChartBarIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  ChartPieIcon,
  TableCellsIcon,
  LightBulbIcon,
  ChatBubbleBottomCenterTextIcon,
  ShareIcon,
  InformationCircleIcon,
  WalletIcon as PlaceholderTokenIcon, 
  ArrowTrendingUpIcon,
  ArrowsRightLeftIcon,
  UsersIcon,
  ClipboardDocumentIcon,
  ArrowTopRightOnSquareIcon,
  MagnifyingGlassIcon, // Added MagnifyingGlassIcon
  LinkIcon, // Added LinkIcon as it's used below
  ShoppingCartIcon, // Added ShoppingCartIcon
} from "@heroicons/react/24/outline";
import toast from "react-hot-toast";

import {
  LineChart,
  Line,
  BarChart,
  Bar, 
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Sector,
  ReferenceLine,
} from "recharts";

const ResultCard = ({
  title,
  icon: Icon,
  children,
  isLoading = false,
  error = null,
  colSpan = "md:col-span-1",
  cardClassName = "",
  titleAction = null,
}) => {
  return (
    <div
      className={`bg-slate-800/60 backdrop-blur-xl border border-slate-700/50 rounded-xl shadow-xl p-0 flex flex-col ${colSpan} ${cardClassName}`}
    >
      {" "}
      {/* Padding diatur di header & content */}
      <div className="flex items-center justify-between text-blue-400 px-5 sm:px-6 pt-5 sm:pt-6 pb-3">
        <div className="flex items-center">
          {Icon && (
            <Icon className="h-5 w-5 sm:h-6 sm:w-6 mr-2.5 flex-shrink-0" />
          )}
          <h2 className="text-lg sm:text-xl font-semibold">{title}</h2>
        </div>
        {titleAction}
      </div>
      <div className="flex-grow px-5 sm:px-6 pb-5 sm:pb-6">
        {" "}
        {/* Padding untuk konten */}
        {isLoading && (
          <div className="flex justify-center items-center h-full min-h-[100px]">
            <Spinner color="blue" />
          </div>
        )}
        {error && !isLoading && (
          <div className="text-red-300 text-sm h-full flex flex-col items-center justify-center">
            <ExclamationTriangleIcon className="h-8 w-8 text-red-400 mb-2" />
            <p className="font-semibold">Error:</p>
            <p className="text-xs text-center">{error}</p>
          </div>
        )}
        {!isLoading && !error && children}
      </div>
    </div>
  );
};
const formatDexStatsForChart = (dexStats) => {
  if (!dexStats) return { priceChangeData: [], volumeData: [] };

  const timeFrames = ["5min", "1h", "4h", "24h"]; // Atau ambil dari keys objek

  const priceChangeData = timeFrames.map((tf) => ({
    timeFrame: tf,
    priceChange: dexStats.pricePercentChange?.[tf] || 0,
    liquidityChange: dexStats.liquidityPercentChange?.[tf] || 0,
  }));

  const volumeData = timeFrames.map((tf) => ({
    timeFrame: tf,
    buyVolume: dexStats.buyVolume?.[tf] || 0,
    sellVolume: dexStats.sellVolume?.[tf] || 0,
    totalVolume: dexStats.totalVolume?.[tf] || 0, // Jika ingin menampilkan total juga
  }));

  const buyerSellerData = timeFrames.map((tf) => ({
    timeFrame: tf,
    buyers: dexStats.buyers?.[tf] || 0,
    sellers: dexStats.sellers?.[tf] || 0,
  }));

  return { priceChangeData, volumeData, buyerSellerData };
};
const CustomBarTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-800/90 backdrop-blur-sm p-3 rounded-lg shadow-lg border border-slate-700">
        <p className="label text-sm font-semibold text-blue-400">{`${label}`}</p>
        {payload.map((entry, index) => (
          <p
            key={`item-${index}`}
            style={{ color: entry.color || entry.payload.fill }}
            className="text-xs"
          >
            {`${entry.name}: ${Number(entry.value).toLocaleString(undefined, {
              maximumFractionDigits: 2,
            })}`}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const PIE_CHART_COLORS = [
  "#FF6B6B",
  "#4ECDC4",
  "#45B7D1",
  "#FED766",
  "#247BA0",
  "#F25F5C",
]; 
const formatHolderSupplyForPieChart = (holderSupply) => {
  if (!holderSupply) return [{ name: "No Data", value: 100, fill: '#475569' }];

  const data = [];
  let accumulatedPercent = 0;
  console.log("[AnalyticsPage] Formatting holder supply for pie chart:", holderSupply);

  if (holderSupply.top10) {
    const top10Percent = parseFloat(holderSupply.top10.supplyPercent);
    if (top10Percent > 0) {
        data.push({ name: `Top 10 Holders (${top10Percent.toFixed(1)}%)`, value: top10Percent });
        accumulatedPercent = top10Percent;
    }
  }
  if (holderSupply.top25) {
    const top25Percent = parseFloat(holderSupply.top25.supplyPercent) - accumulatedPercent;
    if (top25Percent > 0.01) { 
        data.push({ name: `Holders 11-25 (${top25Percent.toFixed(1)}%)`, value: top25Percent });
        accumulatedPercent += top25Percent;
    }
  }
  if (holderSupply.top50) {
    const top50Percent = parseFloat(holderSupply.top50.supplyPercent) - accumulatedPercent;
    if (top50Percent > 0.01) {
        data.push({ name: `Holders 26-50 (${top50Percent.toFixed(1)}%)`, value: top50Percent });
        accumulatedPercent += top50Percent;
    }
  }

  if (accumulatedPercent < 100) {
    const othersPercent = 100 - accumulatedPercent;
    if (othersPercent > 0.01) {
        data.push({ name: `Other Holders (${othersPercent.toFixed(1)}%)`, value: othersPercent });
    }
  }
  
  if (data.length === 0 || data.every(d => d.value === 0)) {
      return [{ name: "No Significant Distribution Data", value: 100, fill: '#475569' }];
  }

  return data.filter(d => d.value > 0); 
};

const AnalyticsPage = () => {
  const { currentAccount } = useWallet();
  const { openCreateAgentModalFromLayout } = useOutletContext() || {}; 
  const navigate = useNavigate(); 

  const [tokenAddressInput, setTokenAddressInput] = useState("");
  const [pairAddressInput, setPairAddressInput] = useState("");

  const [analysisResult, setAnalysisResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [activePieIndex, setActivePieIndex] = useState(0);

  const onPieEnter = useCallback((_, index) => {
    setActivePieIndex(index);
  }, []);



  const handleAnalyze = async () => {
    if (!tokenAddressInput.trim()) {
      toast.error("Please enter a token contract address.");
      return;
    }

    const analysisTypesToRequest = [
      "general_token_analytics",
      "volume_stats",
      "general_ai_info",
      "ai_summary",
      "dex_pair_stats",
    ];

    setIsLoading(true);
    setError("");
    setAnalysisResult(null);
    const analysisToastId = toast.loading(
      `Analyzing token on Solana...`
    );
    try {
      const result = await analyzeTokenData(
        tokenAddressInput.trim(),
        'solana',
        analysisTypesToRequest,
        pairAddressInput.trim() || null,
        currentAccount
      );
      setAnalysisResult(result);
      toast.success("Token analysis complete!", { id: analysisToastId });
    } catch (err) {
      const errorMessage = err.message || "An error occurred during analysis.";
      setError(errorMessage);
      toast.error(errorMessage, { id: analysisToastId });
    } finally {
      setIsLoading(false);
    }
  };

  const displayData = (data, fallback = "N/A") => data || fallback;
  const displayPrice = (price, decimals = 6) =>
    price
      ? `$${Number(price).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: decimals,
        })}`
      : "N/A";
  const formatDate = (isoString) =>
    isoString
      ? new Date(isoString).toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        })
      : "N/A";


  const copyToClipboard = (text, type = "Text") => {
    if (navigator.clipboard && text) {
      navigator.clipboard
        .writeText(text)
        .then(() => toast.success(`${type} copied to clipboard!`))
        .catch((err) => toast.error(`Failed to copy ${type.toLowerCase()}.`));
    } else {
      toast.error("Copying to clipboard is not supported or text is empty.");
    }
  };



  const renderActiveShape = (props) => {
    const { cx, cy, midAngle, innerRadius, outerRadius, startAngle, endAngle, fill, payload } = props;
    const RADIAN = Math.PI / 180;
    const sin = Math.sin(-RADIAN * midAngle);
    const cos = Math.cos(-RADIAN * midAngle);
    const x = cx + (outerRadius + 10) * cos;
    const y = cy + (outerRadius + 10) * sin;

    return (
      <g>
        <text x={x} y={y} fill="#fff" textAnchor={cos >= 0 ? "start" : "end"} dominantBaseline="central">
          {payload.name}
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
      </g>
    );
  }


  return (
    <div className="space-y-6 md:space-y-8 animate-fadeIn">
      {/* Input Section */}
      <div className="bg-slate-800/50 backdrop-blur-lg border border-slate-700/40 rounded-2xl shadow-2xl p-6 sm:p-8">
        <div className="flex items-center mb-8">
          {" "}
          <MagnifyingGlassIcon className="h-8 w-8 sm:h-9 sm:w-9 text-blue-500 mr-3 sm:mr-4 flex-shrink-0" />
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-100">
            Token Deep Analytics
          </h1>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-x-6 gap-y-6 mb-8 items-end">
          <div className="md:col-span-3">
            <label
              htmlFor="tokenAddress"
              className="block text-xs font-medium text-slate-400 mb-1.5"
            >
              Token Contract Address
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none">
                <LinkIcon className="h-5 w-5 text-slate-500" />
              </div>
              <input
                type="text"
                name="tokenAddress"
                id="tokenAddress"
                value={tokenAddressInput}
                onChange={(e) => setTokenAddressInput(e.target.value)}
                className="block w-full pl-10 pr-3 py-3 text-sm rounded-lg 
                               bg-slate-900/70 border-slate-700 text-slate-200 placeholder-slate-500
                               focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500
                               transition duration-150 ease-in-out shadow-sm"
                placeholder="Enter Solana token address"
              />
            </div>
  
          </div>

          <div className="md:col-span-2">
            <label
              className="block text-xs font-medium text-slate-400 mb-1.5"
            >
              Blockchain
            </label>
            <div className="mt-0 block w-full pl-3.5 pr-10 py-3 text-sm rounded-lg shadow-sm 
                         bg-slate-900/70 border-slate-700 text-slate-200 cursor-not-allowed opacity-75">
              Solana
            </div>
          </div>
        </div>

        <div className="mt-8">
          <Button
            className="flex items-center
           justify-center w-full sm:w-auto !py-3 px-10 
           text-base font-semibold
            bg-gradient-to-r from-blue-500 via-red-500 to-pink-600 
                       hover:from-blue-600 hover:via-red-600 hover:to-pink-700 
                       border-transparent rounded-lg shadow-lg hover:shadow-red-500/50 
                       transform hover:scale-105 transition-all duration-200 ease-in-out
                       focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-slate-900 focus:ring-blue-400
           "
            onClick={handleAnalyze}
            isLoading={isLoading}
            disabled={isLoading}
          >
            <DocumentChartBarIcon className="h-5 w-5 mr-2.5" />
            Analyze Token
          </Button>

          {error && !isLoading && (
            <div className="mt-5 w-full p-3.5 bg-red-800/40 text-red-300 border border-red-700/60 rounded-lg flex items-center text-sm shadow-md">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2.5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </div>
      </div>

      {isLoading && (
        <div className="flex flex-col items-center justify-center py-20 text-slate-400">
          <Spinner size="lg" color="blue" />
          <p className="mt-4 text-lg">
            Fetching data and analyzing... this may take a moment.
          </p>
        </div>
      )}

      {analysisResult && !isLoading && (
        <div className="space-y-6 md:space-y-8">
          <ResultCard title="Token Information" icon={InformationCircleIcon} colSpan="md:col-span-3">
            <div className="flex flex-col space-y-8">
              {/* Header Row: Identity & Price & Actions */}
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6 border-b border-slate-700/50 pb-6">
                <div className="flex items-center gap-5">
                  {analysisResult.metadata?.logo ? (
                    <img
                      src={analysisResult.metadata.logo}
                      alt={`${analysisResult.metadata.name || "Token"} logo`}
                      className="h-20 w-20 rounded-2xl object-contain bg-slate-800 p-1 border border-slate-600 shadow-lg"
                    />
                  ) : (
                    <div className="h-20 w-20 rounded-2xl bg-slate-800 flex items-center justify-center border border-slate-600 shadow-lg">
                      <PlaceholderTokenIcon className="h-10 w-10 text-slate-500" />
                    </div>
                  )}
                  <div>
                    <div className="flex items-baseline gap-3">
                      <h3 className="text-3xl font-bold text-white tracking-tight">
                        {displayData(analysisResult.metadata?.name)}
                      </h3>
                      <span className="text-lg font-semibold text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded">
                        {displayData(analysisResult.metadata?.symbol)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-2 text-sm text-slate-400 group cursor-pointer"
                         onClick={() => copyToClipboard(analysisResult.metadata?.address, "Token Address")}>
                      <span className="font-mono bg-slate-900/50 px-2 py-1 rounded border border-slate-700/50 group-hover:border-blue-500/50 transition-colors">
                        {displayData(analysisResult.metadata?.address)}
                      </span>
                      <ClipboardDocumentIcon className="h-4 w-4 text-slate-500 group-hover:text-blue-400 transition-colors" />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-3 w-full md:w-auto">
                  <div className="text-4xl font-bold text-emerald-400 tracking-tight">
                    {displayPrice(analysisResult.price?.usdPrice, 8)}
                  </div>
                  <div className="flex gap-3 w-full md:w-auto">
                    <a
                      href={`https://solscan.io/token/${analysisResult.metadata?.address}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-600 text-slate-200 text-sm font-medium rounded-lg transition-all border border-slate-600 hover:border-slate-500"
                    >
                      <LinkIcon className="h-4 w-4" /> Solscan
                    </a>
                    {analysisResult.price?.exchangeName && (
                      <Link
                        to={`https://jup.ag/swap/SOL-${analysisResult.metadata?.address}`}
                        target="_blank"
                        className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 bg-teal-500/10 hover:bg-teal-500/20 text-teal-400 text-sm font-medium rounded-lg transition-all border border-teal-500/20 hover:border-teal-500/40"
                      >
                        <ShoppingCartIcon className="h-4 w-4" /> Trade on Jupiter
                      </Link>
                    )}
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-900/40 p-4 rounded-xl border border-slate-700/30">
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">Market Cap</p>
                  <p className="text-lg font-semibold text-slate-200">{displayPrice(analysisResult.metadata?.market_cap)}</p>
                </div>
                <div className="bg-slate-900/40 p-4 rounded-xl border border-slate-700/30">
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">24h Volume</p>
                  <p className="text-lg font-semibold text-slate-200">{displayData(analysisResult.price?.volume24h)}</p>
                </div>
                <div className="bg-slate-900/40 p-4 rounded-xl border border-slate-700/30">
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">Decimals</p>
                  <p className="text-lg font-semibold text-slate-200">{displayData(analysisResult.metadata?.decimals)}</p>
                </div>
                <div className="bg-slate-900/40 p-4 rounded-xl border border-slate-700/30">
                  <p className="text-xs text-slate-500 uppercase tracking-wider font-semibold mb-1">Token Age</p>
                  <p className="text-lg font-semibold text-slate-200">
                    {displayData(analysisResult.metadata?.token_age_in_days) !== "N/A"
                      ? `${displayData(analysisResult.metadata?.token_age_in_days)} days`
                      : "N/A"}
                  </p>
                </div>
              </div>

              {/* AI Summary & Charts Section */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                {/* AI Summary Column */}
                <div className="lg:col-span-2 flex flex-col">
                  {analysisResult.aiInsights?.summary ? (
                    <div className="bg-gradient-to-br from-slate-800 to-slate-900/80 rounded-xl border border-slate-700/50 p-6 h-full shadow-inner">
                      <h4 className="text-lg font-semibold text-blue-300 mb-4 flex items-center gap-2">
                        <LightBulbIcon className="h-5 w-5" /> AI Executive Summary
                      </h4>
                      <div className="prose prose-invert max-w-none">
                        <p className="text-slate-300 leading-relaxed whitespace-pre-line text-sm md:text-base">
                          {analysisResult.aiInsights.summary}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full bg-slate-800/30 rounded-xl border border-slate-700/30 p-6 text-slate-500 italic">
                      No AI Summary available for this token.
                    </div>
                  )}
                </div>

                {/* Holder Chart Column */}
                <div className="lg:col-span-1">
                  {analysisResult.tokenAnalytics?.tokenholderStats?.holderSupply ? (
                    <div className="bg-slate-800/30 rounded-xl border border-slate-700/30 p-4 h-full flex flex-col">
                      <h4 className="text-sm font-semibold text-slate-400 mb-4 flex items-center gap-2 uppercase tracking-wider">
                        <ChartPieIcon className="h-4 w-4" /> Holder Distribution
                      </h4>
                      <div className="flex-1 min-h-[250px]">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              activeIndex={activePieIndex}
                              activeShape={renderActiveShape}
                              data={formatHolderSupplyForPieChart(
                                analysisResult.tokenAnalytics.tokenholderStats.holderSupply
                              )}
                              cx="50%"
                              cy="50%"
                              innerRadius={50}
                              outerRadius={70}
                              dataKey="value"
                              onMouseEnter={onPieEnter}
                              paddingAngle={3}
                            >
                              {formatHolderSupplyForPieChart(
                                analysisResult.tokenAnalytics.tokenholderStats.holderSupply
                              ).map((entry, index) => (
                                <Cell
                                  key={`cell-${index}`}
                                  fill={PIE_CHART_COLORS[index % PIE_CHART_COLORS.length]}
                                  stroke="none"
                                />
                              ))}
                            </Pie>
                            <Tooltip
                              contentStyle={{
                                backgroundColor: "rgba(15, 23, 42, 0.95)",
                                borderRadius: "0.5rem",
                                border: "1px solid rgba(51, 65, 85, 0.5)",
                                color: "#e2e8f0",
                                fontSize: "12px",
                              }}
                              formatter={(value, name) => [
                                `${Number(value).toFixed(2)}%`,
                                name.split("(")[0].trim(),
                              ]}
                            />
                            <Legend
                              wrapperStyle={{ fontSize: "10px", color: "#94a3b8" }}
                              iconSize={8}
                              layout="horizontal"
                              verticalAlign="bottom"
                              align="center"
                            />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-center justify-center h-full bg-slate-800/30 rounded-xl border border-slate-700/30 p-6 text-slate-500 text-sm">
                      Holder data unavailable
                    </div>
                  )}
                </div>
              </div>
            </div>
          </ResultCard>

          {analysisResult?.tokenAnalytics?.dexPairStats && (
            <>
              <ResultCard
                title={`DEX Pair Stats: ${
                  analysisResult.tokenAnalytics.dexPairStats.pairLabel ||
                  analysisResult.tokenAnalytics.dexPairStats.pairAddress.substring(
                    0,
                    10
                  ) + "..."
                } on ${analysisResult.tokenAnalytics.dexPairStats.exchange}`}
                icon={ChartBarIcon}
                colSpan="lg:col-span-3" 
              >
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-slate-300">
                  <div>
                    <p>
                      <strong>Pair Address:</strong>{" "}
                      <span className="font-mono text-xs text-slate-400 break-all">
                        {analysisResult.tokenAnalytics.dexPairStats.pairAddress}
                      </span>
                    </p>
                    <p>
                      <strong>Current USD Price (Pair):</strong>{" "}
                      <span className="font-semibold text-emerald-400">
                        {displayPrice(
                          analysisResult.tokenAnalytics.dexPairStats
                            .currentUsdPrice,
                          8
                        )}
                      </span>
                    </p>
                    <p>
                      <strong>Total Liquidity (USD):</strong>{" "}
                      <span className="font-semibold">
                        {displayPrice(
                          analysisResult.tokenAnalytics.dexPairStats
                            .totalLiquidityUsd
                        )}
                      </span>
                    </p>
                  </div>
                  <div>
                    <p>
                      <strong>Token 0 Price (USD):</strong>{" "}
                      {displayPrice(
                        analysisResult.tokenAnalytics.dexPairStats.token0Price
                          ?.usdPrice ||
                          analysisResult.tokenAnalytics.dexPairStats
                            .token0Price,
                        8
                      )}
                    </p>
                    <p>
                      <strong>Token 1 Price (USD):</strong>{" "}
                      {displayPrice(
                        analysisResult.tokenAnalytics.dexPairStats.token1Price
                          ?.usdPrice ||
                          analysisResult.tokenAnalytics.dexPairStats
                            .token1Price,
                        8
                      )}
                    </p>
                    <p>
                      <strong>Pair Created:</strong>{" "}
                      {formatDate(
                        analysisResult.tokenAnalytics.dexPairStats.pairCreated
                      )}
                    </p>
                  </div>
                </div>
              </ResultCard>

              <ResultCard
                title="Price & Liquidity Change (%)"
                icon={ArrowTrendingUpIcon}
                colSpan="lg:col-span-3"
                cardClassName="min-h-[380px]"
              >
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={
                      formatDexStatsForChart(
                        analysisResult.tokenAnalytics.dexPairStats
                      ).priceChangeData
                    }
                    margin={{ top: 5, right: 20, left: -5, bottom: 20 }}
                  >
                    <CartesianGrid
                      strokeDasharray="2 2"
                      stroke="#334155"
                      strokeOpacity={0.4}
                    />
                    <XAxis
                      dataKey="timeFrame"
                      stroke="#94a3b8"
                      tick={{ fontSize: 10 }}
                      axisLine={{ stroke: "#475569" }}
                      tickLine={false}
                    />
                    <YAxis
                      stroke="#94a3b8"
                      tick={{ fontSize: 10 }}
                      axisLine={{ stroke: "#475569" }}
                      tickLine={false}
                      tickFormatter={(value) => `${value.toFixed(1)}%`}
                    />
                    <Tooltip content={<CustomBarTooltip />} />
                    <Legend
                      wrapperStyle={{
                        fontSize: "11px",
                        color: "#cbd5e1",
                        paddingTop: "5px",
                      }}
                      iconSize={8}
                      verticalAlign="bottom"
                    />
                    <ReferenceLine
                      y={0}
                      stroke="#64748b"
                      strokeDasharray="2 2"
                      strokeOpacity={0.7}
                    />{" "}
                    <Bar
                      dataKey="priceChange"
                      name="Price %"
                      fill="#F59E0B"
                      radius={[4, 4, 0, 0]}
                      barSize={20}
                    />
                    <Bar
                      dataKey="liquidityChange"
                      name="Liquidity %"
                      fill="#10B981"
                      radius={[4, 4, 0, 0]}
                      barSize={20}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </ResultCard>

              <ResultCard
                title="Trading Volume (Buy vs Sell)"
                icon={ArrowsRightLeftIcon}
                colSpan="lg:col-span-2"
                cardClassName="min-h-[380px]"
              >
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={
                      formatDexStatsForChart(
                        analysisResult.tokenAnalytics.dexPairStats
                      ).volumeData
                    }
                    margin={{ top: 5, right: 20, left: -5, bottom: 20 }}
                    barCategoryGap="20%"
                  >
                    <CartesianGrid
                      strokeDasharray="2 2"
                      stroke="#334155"
                      strokeOpacity={0.4}
                    />
                    <XAxis
                      dataKey="timeFrame"
                      stroke="#94a3b8"
                      tick={{ fontSize: 10 }}
                      axisLine={{ stroke: "#475569" }}
                      tickLine={false}
                    />
                    <YAxis
                      stroke="#94a3b8"
                      tick={{ fontSize: 10 }}
                      axisLine={{ stroke: "#475569" }}
                      tickLine={false}
                      tickFormatter={(value) => value.toLocaleString()}
                    />
                    <Tooltip content={<CustomBarTooltip />} />
                    <Legend
                      wrapperStyle={{
                        fontSize: "11px",
                        color: "#cbd5e1",
                        paddingTop: "5px",
                      }}
                      iconSize={8}
                      verticalAlign="bottom"
                    />
                    <Bar
                      dataKey="buyVolume"
                      name="Buy Volume"
                      stackId="a"
                      fill="#22C55E"
                      radius={[4, 4, 0, 0]}
                    />{" "}
                    <Bar
                      dataKey="sellVolume"
                      name="Sell Volume"
                      stackId="a"
                      fill="#EF4444"
                      radius={[4, 4, 0, 0]}
                    />{" "}
                  </BarChart>
                </ResponsiveContainer>
              </ResultCard>

              <ResultCard
                title="Traders Activity (Buyers vs Sellers)"
                icon={UsersIcon}
                colSpan="lg:col-span-1"
                cardClassName="min-h-[380px]"
              >
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart
                    data={
                      formatDexStatsForChart(
                        analysisResult.tokenAnalytics.dexPairStats
                      ).buyerSellerData
                    }
                    margin={{ top: 5, right: 20, left: -5, bottom: 20 }}
                  >
                    <CartesianGrid
                      strokeDasharray="2 2"
                      stroke="#334155"
                      strokeOpacity={0.4}
                    />
                    <XAxis
                      dataKey="timeFrame"
                      stroke="#94a3b8"
                      tick={{ fontSize: 10 }}
                      axisLine={{ stroke: "#475569" }}
                      tickLine={false}
                    />
                    <YAxis
                      stroke="#94a3b8"
                      tick={{ fontSize: 10 }}
                      axisLine={{ stroke: "#475569" }}
                      tickLine={false}
                      allowDecimals={false}
                    />
                    <Tooltip content={<CustomBarTooltip />} />
                    <Legend
                      wrapperStyle={{
                        fontSize: "11px",
                        color: "#cbd5e1",
                        paddingTop: "5px",
                      }}
                      iconSize={8}
                      verticalAlign="bottom"
                    />
                    <Bar
                      dataKey="buyers"
                      name="Buyers"
                      fill="#3B82F6"
                      radius={[4, 4, 0, 0]}
                      barSize={15}
                    />{" "}
                    {/* Biru untuk buyers */}
                    <Bar
                      dataKey="sellers"
                      name="Sellers"
                      fill="#F43F5E"
                      radius={[4, 4, 0, 0]}
                      barSize={15}
                    />{" "}
                    {/* Rose untuk sellers */}
                  </BarChart>
                </ResponsiveContainer>
              </ResultCard>

              {/* AI Analysis untuk DEX Pair Stats */}
              {analysisResult.aiInsights?.dexPairAnalysis && (
                <ResultCard
                  title="AI Analysis for DEX Pair"
                  icon={LightBulbIcon}
                  colSpan="lg:col-span-3"
                >
                  <p className="text-slate-200 text-sm italic leading-relaxed whitespace-pre-line custom-scrollbar max-h-60 overflow-y-auto pr-2">
                    {analysisResult.aiInsights.dexPairAnalysis}
                  </p>
                </ResultCard>
              )}

              {analysisResult.aiInsights?.generalInfo && (
                <ResultCard title="AI General Insights" icon={LightBulbIcon}>
                  <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-line custom-scrollbar max-h-60 overflow-y-auto pr-2">
                    {analysisResult.aiInsights.generalInfo}
                  </p>
                </ResultCard>
              )}

      

              {analysisResult.tokenAnalytics?.recentTransfers &&
                analysisResult.tokenAnalytics.recentTransfers.length > 0 && (
                  <ResultCard
                    title="Recent Transfers"
                    icon={TableCellsIcon}
                    colSpan="md:col-span-2 lg:col-span-3"
                    cardClassName="max-h-[500px] flex flex-col overflow-hidden"
                  >
                    {" "}
                    {/* max-h dan flex-col untuk tabel */}
                    <div className="overflow-x-auto custom-scrollbar flex-grow">
                      <table className="w-full text-sm text-left text-slate-300 table-fixed">
                        <thead className="text-xs text-slate-400 uppercase bg-slate-700/50 sticky top-0">
                          <tr>
                            <th scope="col" className="px-4 py-3 w-1/4">
                              From
                            </th>
                            <th scope="col" className="px-4 py-3 w-1/4">
                              To
                            </th>
                            <th scope="col" className="px-4 py-3 w-1/4">
                              Value
                            </th>
                            <th scope="col" className="px-4 py-3 w-1/4">
                              Time
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-700">
                          {analysisResult.tokenAnalytics.recentTransfers
                            .slice(0, 10)
                            .map((tx, index) => (
                              <tr
                                key={tx.transaction_hash || index}
                                className="hover:bg-slate-700/30"
                              >
                                <td
                                  className="px-4 py-2.5 truncate font-mono text-xs"
                                  title={tx.from}
                                >
                                  <a
                                    href={`https://solscan.io/account/${tx.from}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-blue-400"
                                  >
                                    {tx.from.substring(0, 6)}...
                                    {tx.from.substring(tx.from.length - 4)}
                                  </a>
                                </td>
                                <td
                                  className="px-4 py-2.5 truncate font-mono text-xs"
                                  title={tx.to}
                                >
                                  <a
                                    href={`https://solscan.io/account/${tx.to}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-blue-400"
                                  >
                                    {tx.to.substring(0, 6)}...
                                    {tx.to.substring(tx.to.length - 4)}
                                  </a>
                                </td>
                                <td className="px-4 py-2.5 truncate">
                                  {parseFloat(tx.value).toLocaleString(
                                    undefined,
                                    {
                                      maximumFractionDigits: 4,
                                    }
                                  )}{" "}
                                  {analysisResult.metadata?.symbol}
                                </td>
                                <td className="px-4 py-2.5 whitespace-nowrap text-xs text-slate-400">
                                  {formatDate(tx.block_timestamp)}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>
                    {analysisResult.tokenAnalytics.recentTransfers.length >
                      10 && (
                      <p className="text-xs text-slate-500 mt-2 text-right">
                        Showing first 10 transfers...
                      </p>
                    )}
                  </ResultCard>
                )}

              {analysisResult.aiInsights?.volumeAnalysis && (
                /* ... AI Volume Analysis Card ... */
                <ResultCard
                  title="AI Volume Analysis"
                  icon={ChartBarIcon}
                  colSpan="md:col-span-2 lg:col-span-3"
                >
                  <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-line custom-scrollbar max-h-60 overflow-y-auto pr-2">
                    {analysisResult.aiInsights.volumeAnalysis}
                  </p>
                </ResultCard>
              )}
              {analysisResult.tokenAnalytics?.dexPairStats && (
                <ResultCard
                  title="DEX Pair Stats Summary" // Changed title for clarity
                  icon={TableCellsIcon}
                  colSpan="md:col-span-2 lg:col-span-3"
                >
                  <div className="overflow-x-auto custom-scrollbar">
                    <table className="w-full text-sm text-left text-slate-300 table-fixed">
                      <thead className="text-xs text-slate-400 uppercase bg-slate-700/50 sticky top-0">
                        <tr>
                          <th scope="col" className="px-4 py-3 w-1/4">
                            Pair Address
                          </th>
                          <th scope="col" className="px-4 py-3 w-1/4">
                            Volume (24h)
                          </th>
                          <th scope="col" className="px-4 py-3 w-1/4">
                            Liquidity
                          </th>
                          <th scope="col" className="px-4 py-3 w-1/4">
                            Price
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-700">
                        {(() => {
                          const dexStatsData =
                            analysisResult.tokenAnalytics.dexPairStats;
                          let pairsArray = [];

                          if (Array.isArray(dexStatsData)) {
                            // Assuming objects in array already have: pair_address, volume_24h, liquidity, price
                            pairsArray = dexStatsData;
                          } else if (
                            dexStatsData &&
                            typeof dexStatsData === "object"
                          ) {
                            // It's a single rich object, transform it for the table
                            pairsArray = [
                              {
                                pair_address: dexStatsData.pairAddress,
                                volume_24h:
                                  (dexStatsData.buyVolume?.["24h"] || 0) +
                                  (dexStatsData.sellVolume?.["24h"] || 0),
                                liquidity: dexStatsData.totalLiquidityUsd,
                                price: dexStatsData.currentUsdPrice,
                              },
                            ];
                          }

                          return pairsArray.map((pair, index) => (
                            <tr
                              key={pair.pair_address || index}
                              className="hover:bg-slate-700/30"
                            >
                              <td
                                className="px-4 py-2.5 truncate font-mono text-xs"
                                title={pair.pair_address}
                              >
                                {pair.pair_address ? (
                                  <a
                                    // TODO: Make explorer link dynamic based on selectedChain
                                    href={`https://solscan.io/account/${pair.pair_address}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-blue-400"
                                  >
                                    {`${pair.pair_address.substring(
                                      0,
                                      6
                                    )}...${pair.pair_address.substring(
                                      pair.pair_address.length - 4
                                    )}`}
                                  </a>
                                ) : (
                                  "N/A"
                                )}
                              </td>
                              <td className="px-4 py-2.5">
                                {displayPrice(pair.volume_24h)}
                              </td>
                              <td className="px-4 py-2.5">
                                {displayPrice(pair.liquidity)}
                              </td>
                              <td className="px-4 py-2.5">
                                {displayPrice(pair.price)}
                              </td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </ResultCard>
              )}
              {analysisResult.aiInsights?.sniperActivityAssessment && (
                /* ... AI Sniper Card ... */
                <ResultCard
                  title="AI Sniper Activity Assessment"
                  icon={ChatBubbleBottomCenterTextIcon}
                  colSpan="md:col-span-2 lg:col-span-3"
                >
                  <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-line custom-scrollbar max-h-60 overflow-y-auto pr-2">
                    {analysisResult.aiInsights.sniperActivityAssessment}
                  </p>
                </ResultCard>
              )}
              {analysisResult.aiInsights?.profitableWalletsInfo && (
                /* ... AI Profitable Wallets Card ... */
                <ResultCard
                  title="AI Profitable Wallets"
                  icon={ChatBubbleBottomCenterTextIcon}
                  colSpan="md:col-span-2 lg:col-span-3"
                >
                  <p className="text-slate-300 text-sm leading-relaxed whitespace-pre-line custom-scrollbar max-h-60 overflow-y-auto pr-2">
                    {analysisResult.aiInsights.profitableWalletsInfo}
                  </p>
                </ResultCard>
              )}

              {analysisResult.crustLink && (
                /* ... Export & Share Card ... */
                <ResultCard
                  title="Export & Share"
                  icon={ShareIcon}
                  colSpan="md:col-span-2 lg:col-span-3"
                >
                  <div className="flex flex-col space-y-4">
                    <p className="text-slate-300 text-sm">
                      You can export this analysis to Crust Network for sharing
                      or further processing.
                    </p>
                    <div className="flex items-center space-x-3">
                      <Button
                        variant="secondary"
                        onClick={() =>
                          copyToClipboard(
                            analysisResult.crustLink,
                            "Crust Link"
                          )
                        }
                        className="!py-2.5 px-4
                    flex items-center space-x-2
                    bg-red-500/90 hover:bg-red-600/90
                    text-slate-100
                    "
                      >
                        <ClipboardDocumentIcon className="h-5 w-5 mr-2" /> Copy
                        Crust Link
                      </Button>
                      <a
                        href={analysisResult.crustLink}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-400 hover:underline flex items-center space-x-1"
                      >
                        <ArrowTopRightOnSquareIcon className="h-5 w-5" />
                        <span>Open in Crust</span>
                      </a>
                    </div>
                  </div>
                </ResultCard>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default AnalyticsPage;
